import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Smartphone, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useLanguage } from '../../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tzs = (n) => `TZS ${Math.round(n || 0).toLocaleString('en-US')}`;
const STATUS = {
  pending_payment: { en: 'Waiting for PIN', sw: 'Inasubiri PIN', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  paid: { en: 'Paid · allocating units', sw: 'Imelipwa · inagawa vipande', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  allocated: { en: 'Units allocated', sw: 'Vipande vimegawiwa', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  failed: { en: 'Failed', sw: 'Imeshindwa', cls: 'bg-red-50 text-red-700 border-red-200' },
  amount_mismatch: { en: 'Under review', sw: 'Inakaguliwa', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const OrderRow = ({ o, language }) => {
  const s = STATUS[o.status] || STATUS.pending_payment;
  return (
    <li className="flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3 border-b border-border/50 py-2.5 last:border-0" data-testid={`utt-order-${o.order_id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium">{o.fund_name} · {tzs(o.amount_tzs)}</p>
        <p className="text-xs text-muted-foreground">
          {o.units_allocated ? `${o.units_allocated} units @ ${o.nav_allocated}` : `≈ ${o.est_units} units @ ${o.nav_at_order}`}
          {o.utt_reference ? ` · UTT ref ${o.utt_reference}` : ''} · {new Date(o.created_at).toLocaleDateString()}
        </p>
      </div>
      <Badge variant="outline" className={`shrink-0 ${s.cls}`} data-testid={`utt-order-status-${o.order_id}`}>{language === 'sw' ? s.sw : s.en}</Badge>
    </li>
  );
};

export const FundAccount = ({ fund, orders, defaultPhone, token, onChange }) => {
  const { t, language } = useLanguage();
  const [amount, setAmount] = useState(fund ? Math.max(fund.min_investment, 50000) : 50000);
  const [phone, setPhone] = useState(defaultPhone || '');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const poll = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };


  const watch = (orderId) => {
    let tries = 0;
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      tries += 1;
      const res = await axios.get(`${API}/utt-onboarding/orders/${orderId}`, auth).catch(() => null);
      const st = res?.data?.status;
      if (st && st !== 'pending_payment') {
        clearInterval(poll.current);
        setPending(null);
        if (st === 'failed') toast.error(t('Payment failed or was cancelled.', 'Malipo yameshindwa au yamesitishwa.'));
        else toast.success(t('Payment received! Added to your portfolio.', 'Malipo yamepokelewa! Yameongezwa kwenye portfolio yako.'));
        onChange();
      }
      if (tries > 40) clearInterval(poll.current);
    }, 4000);
  };

  // Resume watching an unfinished payment after reload / return visit.
  useEffect(() => {
    const open = orders.find((o) => o.status === 'pending_payment');
    if (open && !pending) { setPending(open); watch(open.order_id); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);
  useEffect(() => () => clearInterval(poll.current), []);

  const pay = async (e) => {
    e.preventDefault();
    if (fund && Number(amount) < fund.min_investment) {
      toast.error(t(`Minimum for ${fund.name} is ${tzs(fund.min_investment)}`, `Kiwango cha chini kwa ${fund.name_sw} ni ${tzs(fund.min_investment)}`));
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/utt-onboarding/orders`, { amount: Number(amount), phone, fund_id: fund?.id }, auth);
      setPending(res.data);
      toast.success(res.data.simulated
        ? t('Preview mode: payment simulated.', 'Hali ya majaribio: malipo ya mfano.')
        : t('Check your phone and enter your mobile money PIN.', 'Angalia simu yako na uweke PIN ya pesa ya simu.'));
      watch(res.data.order_id);
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Could not start payment.', 'Imeshindwa kuanzisha malipo.'));
    } finally {
      setBusy(false);
    }
  };

  const demoConfirm = async () => {
    await axios.post(`${API}/utt-onboarding/orders/${pending.order_id}/demo-confirm`, {}, auth).catch(() => toast.error('Demo confirm failed'));
  };

  return (
    <div className="space-y-5" data-testid="utt-fund-account">
      <form onSubmit={pay} className="rounded-2xl border border-border/60 p-4 space-y-3">
        <p className="text-sm font-semibold">{t(`Invest in ${fund?.name || 'your fund'}`, `Wekeza kwenye ${fund?.name_sw || 'mfuko wako'}`)}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-muted-foreground">{t('Amount (TZS)', 'Kiasi (TZS)')}
            <Input type="number" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="utt-pay-amount" />
          </label>
          <label className="text-xs text-muted-foreground">{t('M-Pesa / Mixx / Airtel / Halopesa number', 'Namba ya M-Pesa / Mixx / Airtel / Halopesa')}
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07XX XXX XXX" className="mt-1" data-testid="utt-pay-phone" />
          </label>
        </div>
        {fund && <p className="text-xs text-muted-foreground">≈ {(Number(amount || 0) / fund.sale_price).toFixed(2)} {t('units at today’s NAV', 'vipande kwa NAV ya leo')} {fund.sale_price} · {t('min', 'chini')} {tzs(fund.min_investment)}</p>}
        <Button type="submit" className="w-full" disabled={busy || !!pending} data-testid="utt-pay-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Smartphone className="mr-2 h-4 w-4" />}
          {t('Pay with mobile money', 'Lipa kwa pesa ya simu')}
        </Button>
        <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          {t('Collected securely by Selcom and invested through CORE Securities (CMSA-licensed) into your UTT AMIS account.',
             'Inakusanywa kwa usalama na Selcom na kuwekezwa kupitia CORE Securities (imesajiliwa CMSA) kwenye akaunti yako ya UTT AMIS.')}
        </p>
      </form>

      {pending && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="utt-pay-pending">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="flex-1">{t('Waiting for you to approve the payment on your phone…', 'Inasubiri uidhinishe malipo kwenye simu yako…')}</span>
          {pending.simulated && <Button size="sm" variant="outline" onClick={demoConfirm} data-testid="utt-demo-confirm">{t('Confirm demo payment', 'Thibitisha malipo ya mfano')}</Button>}
        </div>
      )}

      {orders.length > 0 && (
        <div className="rounded-2xl border border-border/60 p-4" data-testid="utt-orders">
          <p className="mb-1 text-sm font-semibold">{t('Your UTT investments via Wekeza-Smart', 'Uwekezaji wako wa UTT kupitia Wekeza-Smart')}</p>
          <ul>{orders.map((o) => <OrderRow key={o.order_id} o={o} language={language} />)}</ul>
        </div>
      )}
    </div>
  );
};
